import React from 'react';
import { useTranslation } from 'react-i18next';

import { GenerateButton } from '../../../blog-editor/components/GenerateButton';
import type { GenerateTarget } from '../../generate-request';
import {
  Blobs,
  GlowAngleProperty,
  Host,
  MenuItem,
  MenuItemLabel,
  MenuPanel,
  Ring,
  Root,
  SrOnly,
  Veil,
  Wash,
} from './GenerateWithLumio.styles';

export type GenerateWithLumioProps = {
  /** Which panel the control sits in; the footer asks whether Lumio writes prose or a FAQ. */
  target: 'search' | 'footer' | 'ai';
  /** Credits per call, once the price table carries it (never zero-filled before that). */
  cost?: number;
  /** A generation for this panel is running. */
  busy: boolean;
  /** Another generation is running, or the panel cannot take one. */
  disabled?: boolean;
  onGenerate: (target: GenerateTarget) => void;
};

/** The footer's two modes, as the menu offers them. */
const FOOTER_MODES: ReadonlyArray<{ label: string; target: GenerateTarget }> = [
  { label: 'Text', target: 'footer-text' },
  { label: 'FAQ', target: 'footer-faq' },
];

/**
 * "Generate with Lumio" in a panel head: the blog editor's Generate pill (Lumio's mark, the gradient ring, the credit
 * cost). On the footer it opens a small menu, Text or FAQ, first. Affordability is not checked here: the page's
 * pre-flight and the service's 402 lead to the credits sheet.
 */
export const GenerateWithLumio = ({
  target,
  cost,
  busy,
  disabled = false,
  onGenerate,
}: GenerateWithLumioProps): React.ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const off = busy || disabled;

  // Dismisses the footer menu on a press outside it or Escape (document listeners, removed when it closes).
  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      const root = rootRef.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const click = (): void => {
    if (off) return;
    if (target === 'footer') setOpen((was) => !was);
    else onGenerate(target);
  };

  const choose = (next: GenerateTarget): void => {
    setOpen(false);
    if (!off) onGenerate(next);
  };

  return (
    <Root ref={rootRef}>
      <GenerateButton label="Generate with Lumio" cost={cost} disabled={off} onClick={click} />
      {open && !off ? (
        <MenuPanel role="menu" aria-label={t('What Lumio writes')} $align="end" $side="below">
          {FOOTER_MODES.map((mode) => (
            <MenuItem
              key={mode.target}
              type="button"
              role="menuitem"
              $destructive={false}
              onClick={() => choose(mode.target)}
            >
              <MenuItemLabel text={mode.label} tag="span" />
            </MenuItem>
          ))}
        </MenuPanel>
      ) : null}
    </Root>
  );
};

export type GenerateHostProps = {
  /** Which panel this is (`data-generate-host`). */
  kind: GenerateWithLumioProps['target'];
  /** Lumio is writing into this panel: the wash covers it. */
  busy: boolean;
  /** The fields still holding what Lumio wrote (highlighted until edited). */
  generated: readonly string[];
  children: React.ReactNode;
};

/**
 * Wraps a panel Lumio writes into: while it writes, the blog's "Lumio is writing this" wash lies over the panel (and
 * a status line is announced); afterwards each field still holding Lumio's text is highlighted.
 */
export const GenerateHost = ({ kind, busy, generated, children }: GenerateHostProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Host
      data-generate-host={kind}
      data-generated={generated.length > 0 ? generated.join(' ') : undefined}
      aria-busy={busy || undefined}
    >
      {children}
      {busy ? (
        <>
          <GlowAngleProperty />
          <Wash aria-hidden="true">
            <Veil />
            <Blobs />
            <Ring />
          </Wash>
          <SrOnly role="status" aria-live="polite">
            {t('Lumio is writing')}
          </SrOnly>
        </>
      ) : null}
    </Host>
  );
};

export default GenerateWithLumio;
